import {
  EnvelopeIcon,
  FolderOpenIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RELATIONSHIP_TYPES } from "../api/relationship.type";
import { addContactRelation } from "../api/relationships.api";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";

import ThemedSpinner from "../components/shared/spinner";
import ThemedSelect from "../components/shared/themed-select";
import Typography from "../components/shared/typography";
import {
  ContactsContextWrapper,
  useContactsContext,
} from "../hooks/ContactsContextWrapper";

import {
  GroupsContextWrapper,
  useGroupsContext,
} from "#/hooks/GroupsContextWrapper";
import { useLoadContacts } from "#/hooks/useLoadContacts";
import { ConfirmationModal } from "../components/shared/ConfirmationModal";

const ContactViewPageChild = () => {
  const navigate = useNavigate();
  const { selectedContact, isLoading, remove } = useContactsContext();
  const { groups } = useGroupsContext();
  const { contacts } = useLoadContacts();
  const { relationshipId } = useParams<{ relationshipId: string }>();
  const [relatedUserId, setRelatedUserId] = useState<string>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  if (isLoading || !selectedContact) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ThemedSpinner size="lg" />
      </div>
    );
  }

  const contactGroups = groups.filter((g) =>
    g.user_group?.some(
      (u) => u.user_id === selectedContact.contact.related_user_id
    )
  );

  const mutualConnections =
    selectedContact.contact.related_user?.mutual_connections ?? [];
  const hasMutualConnections = mutualConnections.length > 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="w-full max-w-2xl mx-auto" variant="cream" elevation={2}>
        <Card.Header className="relative text-center" variant="cream">
          <SubmitButton
            size="sm"
            iconOnly
            color="cream"
            className="absolute top-1 right-1"
            onClick={() => navigate(`/contacts/update/${relationshipId}`)}
            icon={<PencilSquareIcon className="w-4 h-4" />}
          />
          <Typography variant="h3" className="text-primary">
            {selectedContact?.contact?.related_user?.first_name}{" "}
            {selectedContact?.contact?.related_user?.last_name}
          </Typography>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="space-y-1">
            <Typography variant="body1" className="text-primary-600">
              Email: {selectedContact?.contact?.related_user?.email}
            </Typography>
            <Typography variant="body1" className="text-primary-600">
              Phone: {selectedContact?.contact?.related_user?.phone}
            </Typography>
            {selectedContact?.contact?.related_user_role && (
              <Typography
                variant="body1"
                className="text-primary-600 capitalize"
              >
                Relationship: {selectedContact?.contact?.related_user_role}
              </Typography>
            )}
          </div>
          {contactGroups?.length > 0 && (
            <div className="space-y-2">
              <Typography variant="subtitle2" className="text-terracotta">
                Groups
              </Typography>
              <div className="flex flex-wrap gap-2">
                {contactGroups.map((g) => (
                  <span
                    key={g.id}
                    className="px-2 py-1 bg-primary-100 text-primary-600 rounded text-sm"
                  >
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {hasMutualConnections && (
            <div className="space-y-2">
              <Typography variant="subtitle2" className="text-terracotta">
                Mutual Connections
              </Typography>
              <div className="flex flex-wrap gap-2">
                {mutualConnections.map((c) => (
                  <span
                    key={c.id}
                    className="px-2 py-1 bg-primary-100 text-primary-600 rounded text-sm"
                  >
                    {c.first_name} {c.last_name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <ThemedSelect
              placeholder="Select related user"
              options={contacts
                .filter((c) => c.id !== selectedContact.contact.id)
                .map((c) => ({
                  value: c.related_user_id,
                  label: `${c.related_user.first_name} ${c.related_user.last_name}`,
                }))}
              value={relatedUserId}
              onChange={(e) => setRelatedUserId(e.target.value)}
            />
            {relatedUserId && (
              <ThemedSelect
                placeholder="Select relationship type"
                options={RELATIONSHIP_TYPES.map((r) => ({
                  value: r.type,
                  label: r.type,
                }))}
                value={selectedContact.contact.related_user_role}
                onChange={(e) => {
                  console.log(e.target.value);
                }}
              />
            )}
            <SubmitButton
              size="sm"
              onClick={async () => {
                if (relatedUserId) {
                  await addContactRelation({
                    contactUserId: selectedContact.contact.id,
                    relatedUserId,
                    relatedUserRole: selectedContact.contact
                      .related_user_role as string,
                  });
                  setRelatedUserId("");
                }
              }}
              isDisabled={!relatedUserId}
            >
              Add Relationship
            </SubmitButton>
          </div>

          <div className="flex flex-col gap-2">
            <SubmitButton
              size="sm"
              color="beige"
              onClick={() => navigate(`/contacts/invite/${relationshipId}`)}
              icon={<EnvelopeIcon className="w-4 h-4" />}
            >
              Invite User
            </SubmitButton>
            <SubmitButton
              size="sm"
              color="cream"
              onClick={() =>
                selectedContact.contact.related_user_id &&
                navigate(
                  `/medical-records/user/${selectedContact.contact.related_user_id}`
                )
              }
              icon={<FolderOpenIcon className="w-4 h-4" />}
            >
              Medical Records
            </SubmitButton>
            <SubmitButton
              size="sm"
              color="cream"
              icon={<TrashIcon className="w-4 h-4" />}
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Remove Contact
            </SubmitButton>
          </div>
        </Card.Body>
      </Card>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          if (selectedContact?.contact.id) {
            remove(selectedContact.contact.id);
          }
          navigate("/contacts");
        }}
        title="Delete Contact"
        message={`Are you sure you want to delete ${selectedContact.contact.related_user.first_name} ${selectedContact.contact.related_user.last_name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        tone="danger"
      />
    </div>
  );
};

const ContactViewPage = () => {
  const { relationshipId } = useParams<{ relationshipId: string }>();
  return (
    <GroupsContextWrapper>
      <ContactsContextWrapper relationshipId={relationshipId}>
        <ContactViewPageChild />
      </ContactsContextWrapper>
    </GroupsContextWrapper>
  );
};

export default ContactViewPage;
